/**
 * RecurringScheduleForm
 * Feature: 005-clinic-scheduling (T010)
 *
 * Form for creating/editing recurring appointment rules.
 */

import { useEffect, useId } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { Loader2 } from 'lucide-react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOverlapCheck } from '../hooks/use-overlap-check'
import { useOverlapPolicy } from '../hooks/use-overlap-policy'
import { useAppointmentLocationSuggestions } from '../hooks/use-appointment-location-suggestions'
import { ProfessionalMultiSelect } from './ProfessionalMultiSelect'
import {
  createRecurringScheduleSchema,
  DAY_OF_WEEK_LABELS,
  FREQUENCY_LABELS,
  type CreateRecurringScheduleFormData,
  type DayOfWeek,
  type Frequency,
  type OverlapCheckResult,
} from '../types/scheduling.types'

interface ProfessionalOption {
  id: string
  full_name: string
}

interface RecurringScheduleFormProps {
  patientId: string
  professionals: ProfessionalOption[]
  onSubmit: (data: CreateRecurringScheduleFormData) => Promise<void>
  onOverlapDetected?: (result: OverlapCheckResult) => void
  isSubmitting?: boolean
  defaultValues?: Partial<CreateRecurringScheduleFormData>
}

export function RecurringScheduleForm({
  patientId,
  professionals,
  onSubmit,
  onOverlapDetected,
  isSubmitting = false,
  defaultValues,
}: RecurringScheduleFormProps) {
  const { checkOverlap, isChecking } = useOverlapCheck()
  const { data: policy } = useOverlapPolicy()
  const locationSuggestions = useAppointmentLocationSuggestions()
  const locationListId = useId().replaceAll(':', '')

  const form = useForm<CreateRecurringScheduleFormData>({
    resolver: zodResolver(createRecurringScheduleSchema),
    defaultValues: {
      patient_id: patientId,
      day_of_week: 1 as DayOfWeek,
      start_time: '',
      end_time: '',
      frequency: 'weekly' as Frequency,
      effective_from: format(new Date(), 'yyyy-MM-dd'),
      effective_until: null,
      location_text: '',
      professional_ids: [],
      ...defaultValues,
    },
  })

  const dayOfWeek = form.watch('day_of_week')
  const startTime = form.watch('start_time')
  const endTime = form.watch('end_time')
  // Serialize the array to a stable string so the effect only re-runs when IDs actually change
  const professionalIdsKey = (form.watch('professional_ids') ?? []).join(',')

  // Real-time overlap check when time/professional fields change
  useEffect(() => {
    const professionalIds = professionalIdsKey.split(',').filter(Boolean)
    if (
      !startTime ||
      !endTime ||
      !professionalIds.length ||
      startTime >= endTime
    )
      return
    if (!policy || policy === 'allowed') return

    // Find the next occurrence of this day_of_week for the check
    const today = new Date()
    const targetDay = Number(dayOfWeek)
    const daysUntil = (targetDay - today.getDay() + 7) % 7 || 7
    const nextDate = new Date(today)
    nextDate.setDate(today.getDate() + daysUntil)
    const sessionDate = format(nextDate, 'yyyy-MM-dd')

    const timer = setTimeout(async () => {
      const result = await checkOverlap(
        {
          professional_ids: professionalIds,
          session_date: sessionDate,
          start_time: startTime,
          end_time: endTime,
        },
        policy
      )
      if (result.has_conflicts) {
        onOverlapDetected?.(result)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [
    dayOfWeek,
    startTime,
    endTime,
    professionalIdsKey,
    policy,
    checkOverlap,
    onOverlapDetected,
  ])

  const handleSubmit = async (data: CreateRecurringScheduleFormData) => {
    await onSubmit(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="day_of_week"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dia da semana</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(Number(v))}
                  value={String(field.value)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o dia" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(
                      Object.entries(DAY_OF_WEEK_LABELS) as [string, string][]
                    ).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Frequência</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a frequência" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(
                      Object.entries(FREQUENCY_LABELS) as [string, string][]
                    ).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="start_time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Horário de início</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="end_time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Horário de término</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="effective_from"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data de início</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="effective_until"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data de término (opcional)</FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="professional_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Profissionais</FormLabel>
              <FormControl>
                <ProfessionalMultiSelect
                  professionals={professionals}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location_text"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Local (opcional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ex.: Sala 2, online, escola..."
                  list={locationListId}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <datalist id={locationListId}>
                {locationSuggestions.map((location) => (
                  <option key={location} value={location} />
                ))}
              </datalist>
              <FormDescription>
                Será aplicado às sessões futuras geradas por esta recorrência.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {isChecking && (
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Verificando conflitos...
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={isSubmitting || isChecking}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar Agendamento
          </Button>
        </div>
      </form>
    </Form>
  )
}
